import { useState } from "react";
import api from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import {
  FileCheck, CheckCircle2, Clock, AlertTriangle, CircleDot,
  Eye, Send, Copy, Tablet, ClipboardCheck, XCircle, RefreshCw, MoreHorizontal, AlertCircle, Baby, Users
} from "lucide-react";

const errorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return fallback;
};

/**
 * Form status types:
 * - not_sent: Required form exists but hasn't been sent/assigned yet
 * - pending: Form was assigned/sent but not submitted
 * - incomplete: Patient started but did not submit (partial save - future feature)
 * - submitted: Patient submitted the form
 * - needs_review: Staff need to review/approve
 * - accepted: Staff reviewed and accepted
 * - rejected: Staff reviewed and rejected
 */
const STATUS_CONFIG = {
  not_sent: {
    label: "Not sent",
    badge: "bg-[#757675]/10 text-[#757675]",
    icon: Clock,
  },
  pending: {
    label: "Pending",
    badge: "bg-amber-50 text-amber-600",
    icon: Clock,
  },
  incomplete: {
    label: "Incomplete",
    badge: "bg-amber-50 text-amber-600",
    icon: CircleDot,
  },
  submitted: {
    label: "Submitted",
    badge: "bg-[#205B5A]/10 text-[#205B5A]",
    icon: CheckCircle2,
  },
  needs_review: {
    label: "Needs review",
    badge: "bg-blue-50 text-blue-600",
    icon: ClipboardCheck,
  },
  accepted: {
    label: "Accepted",
    badge: "bg-emerald-50 text-emerald-600",
    icon: CheckCircle2,
  },
  rejected: {
    label: "Rejected",
    badge: "bg-red-50 text-red-600",
    icon: XCircle,
  },
};

// Helper function to get status for individual form assignment
const getFormStatus = (fa) => {
  if (fa.submission) {
    const subStatus = fa.submission.status || "completed";
    if (subStatus === "needs_review") return "needs_review";
    if (subStatus === "accepted") return "accepted";
    if (subStatus === "rejected") return "rejected";
    return "submitted";
  }
  if (fa.status === "sent" || fa.status === "resent") {
    return "pending";
  }
  return "not_sent";
};

// Status Badge component (moved outside)
function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 w-fit ${config.badge}`}>
      <Icon className="w-2.5 h-2.5" />
      {config.label}
    </span>
  );
}

// Overall Status Badge component (moved outside)
function OverallStatusBadge({ overallStatus }) {
  const type = overallStatus.type;
  let classes = "bg-[#757675]/10 text-[#757675]";
  let Icon = Clock;
  
  if (type === "complete") {
    classes = "bg-emerald-50 text-emerald-600";
    Icon = CheckCircle2;
  } else if (type === "partial") {
    classes = "bg-amber-50 text-amber-600";
    Icon = CircleDot;
  } else if (type === "pending") {
    classes = "bg-amber-50 text-amber-600";
    Icon = Clock;
  } else if (type === "review") {
    classes = "bg-blue-50 text-blue-600";
    Icon = ClipboardCheck;
  }
  
  return (
    <span className={`ml-auto text-[9px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${classes}`}>
      <Icon className="w-2.5 h-2.5" />
      {overallStatus.text}
    </span>
  );
}

// Form Row component (moved outside)
function FormRow({
  fa,
  expandedActions,
  setExpandedActions,
  sendingForm,
  copiedLink,
  rowMessage,
  manualLink,
  patientEmail,
  onSendForm,
  onCopyLink,
  onOpenDeviceForm,
  onViewSubmission,
}) {
  const status = getFormStatus(fa);
  const isExpanded = expandedActions === fa.id;

  return (
    <div 
      className="py-2.5 border-t border-[#1A1B1A]/[0.04] first:border-0"
      data-testid={`form-row-${fa.id}`}
    >
      <div className="flex items-start gap-2">
        {/* Status icon */}
        {fa.submission ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-[#205B5A] flex-shrink-0 mt-0.5" />
        ) : (
          <div className="w-3.5 h-3.5 rounded-full border-2 border-[#1A1B1A]/15 flex-shrink-0 mt-0.5" />
        )}
        
        {/* Form info */}
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-medium text-[#1A1B1A] truncate">
            {fa.form_name || "Unknown form"}
          </p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <StatusBadge status={status} />
            {fa.assignment_reasons?.length > 0 && (
              <span className="text-[8px] text-[#757675] px-1 py-0.5 rounded bg-[#FAF9F6]">
                {fa.assignment_reasons.includes("all_appointments") ? "All appts" : 
                 fa.assignment_reasons.includes("appointment_type") ? "Appt type" :
                 fa.assignment_reasons.includes("new_patient") ? "New patient" :
                 fa.assignment_reasons.includes("under_18") ? "Under 18" :
                 fa.assignment_reasons[0]}
              </span>
            )}
            {fa.submission?.created_at && (
              <span className="text-[9px] text-[#757675]">
                {new Date(fa.submission.created_at).toLocaleDateString("en-GB", {
                  day: "numeric", month: "short"
                })}
              </span>
            )}
          </div>
        </div>

        {/* Primary action button */}
        <div className="flex items-center gap-1">
          {!fa.submission ? (
            <button
              onClick={() => onOpenDeviceForm?.(fa)}
              className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544] px-2 py-1 rounded-md hover:bg-[#205B5A]/5 flex items-center gap-1 transition-colors"
              data-testid={`complete-on-device-${fa.id}`}
            >
              <Tablet className="w-3 h-3" />
              Complete on this device
            </button>
          ) : (
            <button
              onClick={() => onViewSubmission?.(fa.submission)}
              className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544] px-2 py-1 rounded-md hover:bg-[#205B5A]/5 flex items-center gap-1 transition-colors"
              data-testid={`view-submission-${fa.id}`}
            >
              <Eye className="w-3 h-3" />
              View
            </button>
          )}

          <button
            onClick={() => setExpandedActions(isExpanded ? null : fa.id)}
            className="p-1 text-[#757675] hover:text-[#1A1B1A] rounded-md hover:bg-[#FAF9F6] transition-colors"
            data-testid={`more-actions-${fa.id}`}
          >
            <MoreHorizontal className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Expanded actions */}
      {isExpanded && (
        <div className="mt-2 ml-5.5 pl-2 border-l-2 border-[#1A1B1A]/[0.06] space-y-1" data-testid={`expanded-actions-${fa.id}`}>
          {!fa.submission ? (
            <>
              <button
                onClick={() => onSendForm(fa)}
                disabled={sendingForm === fa.id || !patientEmail}
                data-testid={`send-form-${fa.id}`}
                className="w-full text-left px-2 py-1.5 rounded-md text-[10px] text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                <Send className="w-3 h-3" />
                {sendingForm === fa.id ? "Sending..." : "Send this form"}
              </button>
              <button
                onClick={() => onCopyLink(fa)}
                className="w-full text-left px-2 py-1.5 rounded-md text-[10px] text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-2 transition-colors"
                data-testid={`copy-form-link-${fa.id}`}
              >
                {copiedLink === fa.id ? (
                  <>
                    <CheckCircle2 className="w-3 h-3 text-[#205B5A]" />
                    <span className="text-[#205B5A]">Form link copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    Copy form link
                  </>
                )}
              </button>
              {!patientEmail && (
                <p className="px-2 text-[9px] text-amber-700" data-testid={`no-email-note-${fa.id}`}>
                  This patient does not have an email address saved.
                </p>
              )}
              {manualLink && (
                <div className="px-2 py-1.5" data-testid={`manual-link-${fa.id}`}>
                  <p className="text-[9px] text-[#757675] mb-1">Copy this link manually:</p>
                  <input
                    readOnly
                    value={manualLink}
                    onFocus={(e) => e.target.select()}
                    className="w-full text-[9px] px-2 py-1 rounded border border-[#1A1B1A]/10 bg-[#FAF9F6] text-[#4A4B4A]"
                  />
                </div>
              )}
              {rowMessage && (
                <p
                  className={`px-2 text-[9px] ${rowMessage.tone === "error" ? "text-red-500" : "text-[#205B5A]"}`}
                  data-testid={`form-row-message-${fa.id}`}
                >
                  {rowMessage.text}
                </p>
              )}
            </>
          ) : (
            <>
              {fa.submission.signature_data && (
                <button
                  onClick={() => onViewSubmission?.(fa.submission)}
                  className="w-full text-left px-2 py-1.5 rounded-md text-[10px] text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-2 transition-colors"
                >
                  <FileCheck className="w-3 h-3" />
                  View signature
                </button>
              )}
              <button
                onClick={() => onSendForm(fa)}
                disabled={sendingForm === fa.id}
                className="w-full text-left px-2 py-1.5 rounded-md text-[10px] text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                <RefreshCw className="w-3 h-3" />
                Request new submission
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MinorNotice({ minor, savingGuardianFlag, toggleGuardianConsent }) {
    if (minor.is_minor) {
      return (
        <div className="mb-2 rounded-lg bg-[#205B5A]/[0.06] border border-[#205B5A]/20 px-3 py-2 flex items-start gap-2" data-testid="drawer-minor-notice">
          <Baby className="w-3.5 h-3.5 text-[#205B5A] flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-[#205B5A] leading-relaxed">
            Patient is under 18{minor.patient_age != null ? ` (age ${minor.patient_age})` : ""}. Forms will
            ask a parent or guardian to give consent and sign.
          </p>
        </div>
      );
    }
    if (!minor.dob_available) {
      return (
        <div className="mb-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2" data-testid="drawer-dob-missing-warning">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-[10px] text-amber-700 leading-relaxed">
              DOB missing, minor status cannot be confirmed.
              {minor.guardian_consent_override ? " Guardian consent is required for this patient (set by staff)." : ""}
            </p>
            <button
              onClick={toggleGuardianConsent}
              disabled={savingGuardianFlag}
              data-testid="toggle-guardian-consent-btn"
              className="mt-1.5 text-[10px] font-medium text-amber-800 underline hover:no-underline disabled:opacity-50"
            >
              {savingGuardianFlag
                ? "Saving..."
                : minor.guardian_consent_override
                ? "Remove guardian consent requirement"
                : "Require guardian consent"}
            </button>
          </div>
        </div>
      );
    }
    if (minor.guardian_consent_override) {
      return (
        <div className="mb-2 rounded-lg bg-[#205B5A]/[0.06] border border-[#205B5A]/20 px-3 py-2 flex items-start gap-2" data-testid="drawer-guardian-override-notice">
          <Baby className="w-3.5 h-3.5 text-[#205B5A] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-[10px] text-[#205B5A] leading-relaxed">
              Guardian consent is required for this patient (set by staff).
            </p>
            <button
              onClick={toggleGuardianConsent}
              disabled={savingGuardianFlag}
              data-testid="toggle-guardian-consent-btn"
              className="mt-1.5 text-[10px] font-medium text-[#205B5A] underline hover:no-underline disabled:opacity-50"
            >
              {savingGuardianFlag ? "Saving..." : "Remove guardian consent requirement"}
            </button>
          </div>
        </div>
      );
    }
  return null;
}

/**
 * PatientFormsSection - Comprehensive form management for appointment drawer
 */
export default function PatientFormsSection({
  appointmentId,
  patientId,
  patientName,
  formAssignments = [],
  formStatus,
  minorInfo,
  patientEmail,
  onRefresh,
  onOpenDeviceForm,
  onViewSubmission,
}) {
  const [expandedActions, setExpandedActions] = useState(null);
  const [sendingForm, setSendingForm] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  const [rowMessages, setRowMessages] = useState({});
  const [manualLinks, setManualLinks] = useState({});
  const [sendAllState, setSendAllState] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [guardianFlag, setGuardianFlag] = useState(null); // local override after staff toggle
  const [savingGuardianFlag, setSavingGuardianFlag] = useState(false);

  const minor = { ...(minorInfo || {}), ...(guardianFlag || {}) };

  const toggleGuardianConsent = async () => {
    if (!patientId) return;
    setSavingGuardianFlag(true);
    try {
      const { data } = await api.post(`/forms/patients/${patientId}/guardian-consent`, {
        required: !minor.guardian_consent_override,
      });
      setGuardianFlag(data);
      onRefresh?.();
    } catch (err) {
      console.error("Failed to update guardian consent requirement:", err);
    }
    setSavingGuardianFlag(false);
  };


  // Compute overall status summary
  const computeOverallStatus = () => {
    if (!formAssignments || formAssignments.length === 0) {
      return { text: "No required forms", type: "none" };
    }

    const total = formAssignments.length;
    const completed = formAssignments.filter(fa => fa.submission).length;
    const pending = total - completed;
    const needsReview = formAssignments.filter(fa => 
      fa.submission && fa.submission.status === "needs_review"
    ).length;

    if (completed === total && needsReview === 0) {
      return { text: "All forms completed", type: "complete" };
    }
    if (needsReview > 0) {
      return { text: `${needsReview} form${needsReview > 1 ? "s" : ""} need${needsReview === 1 ? "s" : ""} review`, type: "review" };
    }
    if (completed > 0 && pending > 0) {
      return { text: `${completed} of ${total} forms completed`, type: "partial" };
    }
    if (pending === total) {
      return { text: `${pending} form${pending > 1 ? "s" : ""} pending`, type: "pending" };
    }
    return { text: "Forms pending", type: "pending" };
  };

  const overallStatus = computeOverallStatus();
  const pendingCount = formAssignments.filter((fa) => !fa.submission).length;

  const setRowMessage = (id, text, tone = "error") => {
    setRowMessages((m) => ({ ...m, [id]: { text, tone } }));
    setTimeout(() => setRowMessages((m) => ({ ...m, [id]: null })), 6000);
  };

  // Send only the selected form
  const handleSendForm = async (fa) => {
    setSendingForm(fa.id);
    try {
      const { data } = await api.post(`/appointments/${appointmentId}/send-form`, {
        form_assignment_id: fa.id,
        template_id: fa.template_id,
      });
      const ok = data.emails_sent || data.emails_queued;
      setRowMessage(fa.id, data.emails_sent ? "Sent to patient" : data.note, ok ? "ok" : "error");
      onRefresh?.();
    } catch (err) {
      setRowMessage(fa.id, errorMessage(err, "Couldn't send this form. Please try again."));
    }
    setSendingForm(null);
  };

  // Send every pending form - explicit, separate action
  const handleSendAll = async () => {
    setSendingAll(true);
    setSendAllState(null);
    try {
      const { data } = await api.post(`/appointments/${appointmentId}/resend-forms`, {});
      setSendAllState({
        tone: data.emails_sent || data.emails_queued ? "ok" : "error",
        text: data.emails_sent
          ? `Sent ${data.emails_sent} form${data.emails_sent > 1 ? "s" : ""} to patient`
          : data.note,
      });
      onRefresh?.();
    } catch (err) {
      setSendAllState({ tone: "error", text: errorMessage(err, "Couldn't send the forms. Please try again.") });
    }
    setSendingAll(false);
    setTimeout(() => setSendAllState(null), 6000);
  };

  // Copy form link - always via the central backend link generator
  const handleCopyLink = async (fa) => {
    setManualLinks((m) => ({ ...m, [fa.id]: null }));
    let link;
    try {
      const res = await api.post("/forms/link", {
        form_assignment_id: fa.id,
        template_id: fa.template_id,
        patient_id: patientId,
        appointment_id: appointmentId,
      });
      link = res.data?.link;
      if (!link) throw new Error("no link returned");
    } catch (err) {
      setRowMessage(fa.id, errorMessage(err, "The secure link could not be created. Please try again."));
      return;
    }

    if (await copyText(link)) {
      setCopiedLink(fa.id);
      setTimeout(() => setCopiedLink(null), 2500);
      return;
    }
    // Link is valid, the browser just blocked clipboard access - let staff copy it by hand
    setManualLinks((m) => ({ ...m, [fa.id]: link }));
    setRowMessage(fa.id, "Your browser blocked the clipboard. Copy the link below.");
  };

  // No forms required
  if (!formAssignments || formAssignments.length === 0) {
    return (
      <div className="bg-white border border-[#1A1B1A]/[0.06] rounded-xl p-3.5 mb-4" data-testid="patient-forms-section">
        <div className="flex items-center gap-2">
          <FileCheck className="w-3.5 h-3.5 text-[#757675]" />
          <span className="text-xs font-semibold text-[#1A1B1A]">Patient forms</span>
          <span className="ml-auto text-[10px] text-[#757675]">No required forms</span>
        </div>
        <div className="mt-2"><MinorNotice minor={minor} savingGuardianFlag={savingGuardianFlag} toggleGuardianConsent={toggleGuardianConsent} /></div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#1A1B1A]/[0.06] rounded-xl p-3.5 mb-4" data-testid="patient-forms-section">
      {/* Header with overall status */}
      <div className="flex items-center gap-2 mb-2">
        <FileCheck className="w-3.5 h-3.5 text-[#205B5A]" />
        <span className="text-xs font-semibold text-[#1A1B1A]">Patient forms</span>
        <OverallStatusBadge overallStatus={overallStatus} />
      </div>

      <MinorNotice minor={minor} savingGuardianFlag={savingGuardianFlag} toggleGuardianConsent={toggleGuardianConsent} />

      {/* Form list */}
      <div className="space-y-0">
        {formAssignments.map(fa => (
          <FormRow 
            key={fa.id} 
            fa={fa}
            expandedActions={expandedActions}
            setExpandedActions={setExpandedActions}
            sendingForm={sendingForm}
            copiedLink={copiedLink}
            rowMessage={rowMessages[fa.id]}
            manualLink={manualLinks[fa.id]}
            patientEmail={patientEmail}
            onSendForm={handleSendForm}
            onCopyLink={handleCopyLink}
            onOpenDeviceForm={onOpenDeviceForm}
            onViewSubmission={onViewSubmission}
          />
        ))}
      </div>

      {/* Missing required forms warning */}
      {overallStatus.type === "pending" && formAssignments.some(fa => !fa.submission) && (
        <div className="mt-3 pt-3 border-t border-[#1A1B1A]/[0.04]">
          <div className="bg-amber-50/50 rounded-lg px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber-700 leading-relaxed">
              Required forms not completed. Complete forms before starting treatment.
            </p>
          </div>
        </div>
      )}

      {/* Quick actions footer */}
      <div className="mt-3 pt-2.5 border-t border-[#1A1B1A]/[0.04] flex flex-wrap gap-2 items-center">
        {pendingCount > 0 && (
          <button
            onClick={handleSendAll}
            disabled={sendingAll || !patientEmail}
            data-testid="send-all-pending-forms-btn"
            className="text-[10px] font-medium text-white bg-[#205B5A] hover:bg-[#174544] px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <Users className="w-3 h-3" />
            {sendingAll ? "Sending..." : `Send all pending forms (${pendingCount})`}
          </button>
        )}
        {formAssignments.some(fa => fa.submission) && (
          <button
            onClick={() => {
              const submittedForm = formAssignments.find(fa => fa.submission);
              if (submittedForm) onViewSubmission?.(submittedForm.submission);
            }}
            className="text-[10px] font-medium text-[#4A4B4A] hover:text-[#1A1B1A] px-2.5 py-1.5 rounded-lg hover:bg-[#FAF9F6] flex items-center gap-1.5 transition-colors"
            data-testid="view-submitted-forms-btn"
          >
            <Eye className="w-3 h-3" />
            View submissions
          </button>
        )}
        {pendingCount > 0 && !patientEmail && (
          <p className="text-[9px] text-amber-700 w-full" data-testid="send-all-no-email-note">
            This patient does not have an email address saved.
          </p>
        )}
        {sendAllState && (
          <p
            className={`text-[9px] w-full ${sendAllState.tone === "error" ? "text-red-500" : "text-[#205B5A]"}`}
            data-testid="send-all-message"
          >
            {sendAllState.text}
          </p>
        )}
      </div>
    </div>
  );
}
